import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EpErrorService, ToastService } from '@isophi/core-ui';
import { ChildProfileDetail, ChildProfileUpdate, ChildrenService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, from, of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { ChildFormComponent } from '../../components/child-form/child-form.component';
import { SaveAllDialogComponent, SaveAllDialogResult } from './save-all-dialog/save-all-dialog.component';

@Component({
  selector: 'app-edit-child',
  templateUrl: './edit-child.component.html',
  styleUrls: ['./edit-child.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditChildComponent implements AfterViewInit, OnDestroy {
  private readonly epErrorService = inject(EpErrorService);
  @ViewChild(ChildFormComponent) childFormComponent: ChildFormComponent;

  child: ChildProfileDetail;

  private _subs: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    public links: RouterLinkFactory,
    private spinner: NgxSpinnerService,
    private childrenService: ChildrenService,
    private translocoService: TranslocoService,
    private toastr: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private modal: NgbModal
  ) {
    this.helperService.setTitle(location.pathname);
  }

  ngAfterViewInit(): void {
    this._subs.push(
      this.route.paramMap.subscribe((params) => {
        const id = params.get('id');
        this._subs.push(
          this.childrenService.childrenRetrieve(id).subscribe((childData: ChildProfileDetail) => {
            this.child = childData;
            this.childFormComponent.setFormData(childData);
            this.helperService.smallTextTitle$.next(childData.firstName + ' ' + childData.lastName);
          })
        );
      })
    );
  }

  editChild(childForm): void {
    this.spinner.show();

    this.childrenService
      .childrenCheckExportChangesCreate(childForm.data, this.child.childId)
      .pipe(
        switchMap((checkResult) => {
          // Show the dialog only when at least one changed field offers a real choice (an existing value
          // was overwritten and its current record is not locked). Otherwise save silently as an update —
          // locked-current changes and empty-field fills are always processed as updates (spec §5.2).
          const hasCorrectableChange = (checkResult.changedExportFields || []).some((f) => f.correctionAllowed && f.latestHistoryDate);
          if (!hasCorrectableChange) {
            return of([] as string[]);
          }
          this.spinner.hide();
          const modalRef = this.modal.open(SaveAllDialogComponent, { size: 'xl', centered: true });
          modalRef.componentInstance.changedFields = checkResult.changedExportFields;
          return from(modalRef.result as Promise<SaveAllDialogResult>).pipe(
            map((result: SaveAllDialogResult) => result.correctionFields),
            catchError(() => EMPTY)
          );
        }),
        switchMap((correctionFields: { [key: string]: string }) => {
          this.spinner.show();
          const body: ChildProfileUpdate = { ...childForm.data };
          if (Object.keys(correctionFields).length) body.correctionFields = correctionFields;
          return this.childrenService.childrenUpdate(body, this.child.childId);
        })
      )
      .subscribe({
        next: () => this.afterSaveSuccess(childForm),
        error: (e) => {
          this.spinner.hide();
          this.epErrorService.handleEpErrorResponse(e, {
            defaultMessage: this.translocoService.translate('child/editChildFailed'),
          });
        },
      });
  }

  private afterSaveSuccess(childForm): void {
    this.toastr.success(this.translocoService.translate('child/editChildSuccess'), this.translocoService.translate('child/editChild'));

    if (childForm.file instanceof Blob) {
      this.childrenService
        .childrenUploadProfileImageUpdateForm(this.child.childId, childForm.file)
        .pipe(
          finalize(() => {
            this.spinner.hide();
            this.childFormComponent.resetChildForm();
            this.router.navigate(this.links.childrenDetail(this.child.childId));
          })
        )
        .subscribe({
          error: () => {
            this.toastr.error(
              this.translocoService.translate('child/uploadImageFailed'),
              this.translocoService.translate('child/editChild')
            );
          },
        });
    } else {
      this.spinner.hide();
      this.childFormComponent.resetChildForm();
      this.router.navigate(this.links.childrenDetail(this.child.childId));
    }
  }

  ngOnDestroy(): void {
    this._subs.forEach((_sub) => _sub.unsubscribe());
    this.helperService.clearSmallTextTitle();
  }
}
